import { inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { endOfDay, endOfMonth, startOfMonth, subMonths } from "date-fns";
import { firstValueFrom, TimeoutError } from "rxjs";
import { timeout } from "rxjs/operators";

import { AuthService } from "@bitwarden/common/abstractions/auth.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { AuthenticationStatus } from "@bitwarden/common/enums/authenticationStatus";
import { PluginConfigService } from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { CalculationPastStoreService } from "@bitwarden/web-vault/app/services/store/calculation-past/calculation-past.store.service";
import { TransactionDirection } from "@bitwarden/web-vault/app/models/enum/transactionDirection";
import { GlossRoutes } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import { BALANCE_DEFINITIONS } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { View } from "@bitwarden/web-vault/app/models/enum/view.enum";
import { BalanceCalculatedUtils } from "@bitwarden/web-vault/app/services/store/calculation-base/balances/balance.calculated.utils";
import { ViewsService } from "@bitwarden/web-vault/app/services/views/views.service";
import { WEB_WORKER_QUEUE } from "@bitwarden/web-vault/app/services/web-worker/WebWorkerQueue";
import { sortTransactionViews } from "@bitwarden/web-vault/app/shared/utils/helper.transactionView/sort";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { monthKey, MonthlySummaryVisibilityService } from "./monthly-summary-visibility.service";

export interface MonthlyStats {
  month: Date;
  totalIn: number;
  totalOut: number;
  openingBalance: number;
  closingBalance: number;
  topCategories: Array<{ name: string; amount: number }>;
  /** Every spending category for the month, largest first (topCategories is its first 5). */
  allCategories: Array<{ name: string; amount: number }>;
  /**
   * Running balance at the end of each day of the month, index 0 = day 1. Covers
   * [month start, effectiveEnd], so an in-progress month is shorter than its calendar length.
   * A day whose balance couldn't be resolved (incomplete worker result) is NaN. Days before the
   * account's first transaction are also NaN ("no account yet", not a balance of 0; check
   * hasAccountActivity). Empty for a future month.
   */
  dailyBalances: number[];
  /**
   * Lowest end-of-day balance in the month, or null when no day has a usable balance. Days before
   * the first entry of any kind (an opening-balance entry included) are ignored — they are NaN
   * ("no account yet"), not a balance.
   */
  lowestBalance: { amount: number; date: Date } | null;
  /**
   * Number of distinct accounts that have at least one transaction (hidden/closed accounts
   * included). Account-level: the same for every month, even one before an account was opened.
   */
  accountCount: number;
  currency: string;
  /**
   * False when the account has no transaction/balance activity on or before this month's end —
   * e.g. the month predates the account's creation (brand-new signup). Distinct from
   * totalIn === 0 && totalOut === 0, which can legitimately happen for an existing account that
   * simply had no activity in an otherwise-valid month.
   */
  hasAccountActivity: boolean;
  /**
   * Note: closingBalance − openingBalance ≠ totalIn − totalOut in months that contain FX
   * revaluations or account transfers. Balances include both (full portfolio picture);
   * totalIn/totalOut exclude revals (user transactions only) and auto-detected transfers
   * (money moved between the user's own accounts, not spending). The difference equals the
   * net revaluation plus the net excluded transfers for the month. UI that displays both
   * sets of numbers should not show a reconciliation row that implies these identities hold.
   */
}

type TxnList = Parameters<BalanceCalculatedUtils["getCurrentBalance"]>[1];

interface BalanceData {
  sortedTxns: TxnList;
  balanceByPosition: number[];
  currency: string;
  accountCount: number;
  // ms timestamp of the earliest real (non-synthetic, non-reval) transaction, or null if the
  // account has none at all. Account-level and month-independent, so it's computed once here
  // rather than re-scanned per month in _computeMonthStats.
  firstRealActivityMs: number | null;
}

interface BalanceDataCache {
  promise: Promise<BalanceData>;
  // null while the fetch is still pending; set to the completion time once it settles.
  settledAt: number | null;
}

// postDate is typed as Date but can be an ISO string at runtime; this helper handles both.
const toPostDateMs = (postDate: Date): number =>
  postDate instanceof Date ? postDate.getTime() : new Date(postDate as unknown as string).getTime();

// Shared by totalIn/totalOut aggregation and the hasAccountActivity check: a transaction only
// counts as real user activity when it's neither a synthetic balance row (BALANCE_DEFINITIONS)
// nor a system-generated FX revaluation (reval transactions carry definition='transaction', so
// c_isRevaluation is the only guard that excludes them).
const isUserTransaction = (txn: TxnList[number]): boolean =>
  !BALANCE_DEFINITIONS.has(txn.definition) && !txn.c_isRevaluation;

@Injectable({ providedIn: "root" })
export class MonthlySummaryService extends BalanceCalculatedUtils {
  private readonly transactionStore = inject(TransactionStoreService);
  private readonly calculationPastStore = inject(CalculationPastStoreService);
  private readonly log = inject(LogService);
  private readonly pluginConfig = inject(PluginConfigService);
  private readonly webWorkerQueue = inject(WEB_WORKER_QUEUE);
  private readonly _visibility = inject(MonthlySummaryVisibilityService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly viewsService = inject(ViewsService);
  private readonly dialogFactory = new DialogFactory();
  private _dialogOpen = false;
  private _dialogRef: { close(): void } | null = null;

  // Beta-only: true counts transfers back into IN/OUT (flipped by the Monthly Summary dialog's
  // beta-gated toggle). Default false = transfers excluded. The general user-facing toggle is the
  // deferred 5.2b follow-up (TMS #13690).
  readonly includeTransfers = signal(false);

  // userStore inherited from BalanceCalculatedUtils

  // Delegates to MonthlySummaryVisibilityService — keeps this service focused on data.
  get shouldShowSummary() {
    return this._visibility.shouldShowSummary;
  }

  /** Whether the Monthly Summary plugin is on — the single gate all three call sites (side
   * menu, mobile more-overview, and the Views auto-open path) should read before showing any
   * trigger or opening the dialog. */
  get enabled(): boolean {
    return this.pluginConfig.isMonthlySummaryPluginEnabled();
  }

  markSummaryAsSeen(seenMonthKey?: string): Promise<void> {
    return this._visibility.markSummaryAsSeen(seenMonthKey);
  }

  /**
   * Opens the monthly summary dialog for the given month (defaulting to the most recently
   * completed month). Safe to call from any page — this service is root-provided and
   * getStatsForMonth() waits on the (also root-provided, bootstrap-initialised) calculation
   * store internally, so callers don't need to be on the Views page for data to be ready. A
   * no-op while the plugin is disabled or a dialog is already open — the single source of truth
   * for both checks, so callers never need to duplicate either guard.
   */
  async open(month = subMonths(new Date(), 1), initialStats?: MonthlyStats): Promise<boolean> {
    if (!this.enabled || this._dialogOpen) {
      return false;
    }
    this._dialogOpen = true;
    // Derived from the same month being opened (rather than computed independently a moment
    // later) so the month marked as seen always matches the month actually shown, even if a
    // rollover happens during the async open.
    const seenMonthKey = monthKey(month);
    try {
      const ref = await this.dialogFactory.openMonthlySummary(month, initialStats);
      this._dialogRef = ref;
      ref.afterClosed().subscribe(async () => {
        this._dialogOpen = false;
        this._dialogRef = null;
        // AppComponent force-closes every dialog on lock/logout. The user didn't dismiss the
        // summary then, so don't mark it as seen (and don't try to save to a locked vault).
        if ((await this.authService.getAuthStatus()) !== AuthenticationStatus.Unlocked) {
          return;
        }
        this.markSummaryAsSeen(seenMonthKey).catch((err) => this.log.error(err));
      });
      return true;
    } catch (err) {
      this._dialogOpen = false;
      this._dialogRef = null;
      this.log.error(err);
      return false;
    }
  }

  // Coalesces only the expensive worker round-trip (not the calculation-readiness wait).
  // Each caller independently waits for isIdleAfterSuccess$ with its own timeout, so a
  // late-joining caller is never locked into the remaining window of an earlier call.
  private _balanceDataCache: BalanceDataCache | null = null;
  // Reuse window for sequential (non-concurrent) callers, e.g. the dialog's loadData() fetching
  // prior/two-months-ago stats right after the auto-open gate already fetched the current month
  // (the gate now hands its own result straight to the dialog — see maybeAutoOpenMonthlySummary
  // in views.component.ts — so this window only needs to cover the *other* months' fetches).
  // There's no cheaper correctness check available: neither store exposes a cycle id/version, and
  // the reval collection gets a new array reference on every successful cycle even when its
  // contents are unchanged, so reference comparison can't distinguish "stale" from "just re-ran
  // with the same data" (see calculation-past.store.service.ts / transaction.store.service.ts).
  // Short enough that a real data change (new sync) landing mid-window is an acceptable, bounded
  // staleness trade-off.
  private static readonly BALANCE_DATA_REUSE_WINDOW_MS = 3_000;

  /**
   * Returns stats for a given calendar month.
   * @param year  Full year, e.g. 2026
   * @param month 0-indexed month number (January = 0, December = 11) — pass `new Date().getMonth()` directly, NOT `getMonth() + 1`
   *
   * Note: for the current in-progress month, stats cover [month start, today] rather than the
   * full month. Completed past months always cover the full calendar month.
   */
  async getStatsForMonth(year: number, month: number): Promise<MonthlyStats> {
    if (month < 0 || month > 11) {
      throw new Error(`getStatsForMonth: month must be 0–11, got ${month}`);
    }
    const data = await this._prepareBalanceData();
    return this._computeMonthStats(year, month, data, this._buildCategoryMap());
  }

  /**
   * Opens the Past view on the transactions page, scoped to the given month (capped at today
   * for an in-progress month) and clamped to the transactions the store actually has.
   *
   * Overwrites the Past view's filters and period — the summary always covers every
   * account/category, so a leftover filter would show a subset of what it counted. Closes the
   * summary dialog (if open) so it doesn't linger over the Views page; that counts as the user
   * having seen the summary. Note the list is not a 1:1 match for the summary's totals: it still
   * shows auto-detected transfers, which the summary excludes unless includeTransfers is on.
   *
   * The filter/period overwrite happens before navigating and is not rolled back if navigation
   * is cancelled (e.g. by a route guard); in that case the dialog is left open.
   *
   * @returns true once the Views page is showing (navigated there, or already on it), false if
   * skipped (future month) or the navigation was cancelled.
   */
  async viewTransactionsForMonth(month: Date): Promise<boolean> {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(monthStart);
    const now = new Date();
    if (monthStart > now) {
      // A future month has no transactions in the Past view, and its range would be inverted.
      this.log.warning(
        `MonthlySummaryService.viewTransactionsForMonth: ${monthStart.toISOString()} is in the future`,
      );
      return false;
    }

    let startDate = monthStart;
    const endDate = monthEnd < now ? monthEnd : now;
    // Keep the start inside the transactions the store has so the datepicker stays consistent.
    // Deliberately NOT clamped to period.options(): those are derived from the *filtered*
    // transactions, so with a narrow filter active they'd cut the month short — and the filter is
    // cleared below. Skipped when the month ends before the first transaction (clamping would
    // invert the range). The end needs no clamp: it is already capped at today.
    const earliestMs = this._earliestTransactionMs();
    if (
      earliestMs !== null &&
      earliestMs > startDate.getTime() &&
      earliestMs <= endDate.getTime()
    ) {
      startDate = new Date(earliestMs);
    }

    // Guarded: clearFilter() is only valid once options are initialised.
    if (this.calculationPastStore.filters.options()) {
      this.calculationPastStore.filters.clearFilter();
    }
    this.calculationPastStore.period.updateSelection({ startDate, endDate, _isDefault: false });
    this.viewsService.view = View.PAST;
    const viewsUrl = this.router.createUrlTree(["/", GlossRoutes.Views]);
    // Already on the Views page (the dialog auto-opens there): the router ignores a same-URL
    // navigation and resolves false, which would leave the dialog open over the updated page.
    const alreadyOnViews = this.router.isActive(viewsUrl, {
      paths: "exact",
      queryParams: "ignored",
      fragment: "ignored",
      matrixParams: "ignored",
    });
    const navigated = alreadyOnViews || (await this.router.navigateByUrl(viewsUrl));
    if (navigated) {
      this._dialogRef?.close();
    }
    return navigated;
  }

  /** Earliest postDate (ms) across all stored transactions, unfiltered; null if there are none. */
  private _earliestTransactionMs(): number | null {
    let earliest: number | null = null;
    for (const t of this.transactionStore.alignedTransactionGeneratedCollection.collection() ??
      []) {
      if (!t.postDate) {
        continue;
      }
      const ms = toPostDateMs(t.postDate);
      if (earliest === null || ms < earliest) {
        earliest = ms;
      }
    }
    return earliest;
  }

  /** Returns stats for the 3 most recently completed months, newest first (index 0 = last month). */
  async get3MonthStats(): Promise<MonthlyStats[]> {
    const data = await this._prepareBalanceData();
    const today = new Date();
    const categoryMap = this._buildCategoryMap();
    return [0, 1, 2].map((offset) => {
      const d = subMonths(today, offset + 1);
      return this._computeMonthStats(d.getFullYear(), d.getMonth(), data, categoryMap);
    });
  }

  private async _prepareBalanceData(): Promise<BalanceData> {
    // Phase 1: Each caller waits for a confirmed idle+successful cycle with its own 30 s
    // timeout. Using isIdleAfterSuccess$ (not isCalculationSuccessful$ alone) ensures the
    // store is idle before reading collections, closing the narrow race where
    // _lastCycleSucceeded$ is true but refreshState has already started a new cycle.
    await this._waitForSuccessfulCycle();

    // Phase 2: Share the expensive worker round-trip across concurrent AND near-simultaneous
    // sequential callers (see BALANCE_DATA_REUSE_WINDOW_MS above). A still-pending fetch
    // (settledAt === null) is always shared, no matter how long it's been running — only a
    // *settled* result ages out after the reuse window.
    const cache = this._balanceDataCache;
    const isPending = cache !== null && cache.settledAt === null;
    const isFreshlySettled =
      cache !== null &&
      cache.settledAt !== null &&
      Date.now() - cache.settledAt < MonthlySummaryService.BALANCE_DATA_REUSE_WINDOW_MS;

    if (isPending || isFreshlySettled) {
      return cache.promise;
    }

    const promise: Promise<BalanceData> = this._fetchBalanceData()
      .then((data) => {
        if (this._balanceDataCache?.promise === promise) {
          this._balanceDataCache.settledAt = Date.now();
        }
        return data;
      })
      .catch((e) => {
        if (this._balanceDataCache?.promise === promise) {
          this._balanceDataCache = null;
        }
        throw e;
      });
    this._balanceDataCache = { promise, settledAt: null };
    return promise;
  }

  private async _waitForSuccessfulCycle(): Promise<void> {
    try {
      await firstValueFrom(this.calculationPastStore.isIdleAfterSuccess$.pipe(timeout(30_000)));
    } catch (e) {
      if (e instanceof TimeoutError) {
        throw new Error(
          "MonthlySummaryService: timed out waiting for a successful calculation cycle (30 s). " +
            "The calculation store may be in a persistent error state.",
        );
      }
      throw e;
    }
  }

  private async _fetchBalanceData(): Promise<BalanceData> {
    const allTransactions = this._getAllTransactionsForBalance();
    // filter() returns a new array; sort in-place on that new array gives us sortedTxns.
    const sortedTxns = allTransactions.filter((t) => !!t.postDate).sort(sortTransactionViews);
    // Use positional indexing rather than ID-keyed lookup: reval TransactionViews share no
    // stable id (constructed via new TransactionView()), so an ID map collapses all revals
    // to one key and returns wrong running balances for multi-currency portfolios.
    // sortedTxns is already sorted; getBalancesByPosition requires pre-sorted input.
    const balanceByPosition = await this.getBalancesByPosition(this.webWorkerQueue, sortedTxns);
    const currency = this.userStore.preferences.preferenceView()?.coreCurrency ?? "AUD";
    // sortedTxns is ascending, so the earliest real (non-synthetic, non-reval) entry tells us
    // whether the account has ever had any activity at all — account-level, not per-month.
    const firstRealActivity = sortedTxns.find(isUserTransaction);
    const firstRealActivityMs = firstRealActivity ? toPostDateMs(firstRealActivity.postDate) : null;
    // Accounts that actually feed the summary (have transactions) — accountViews() can also hold
    // empty/hidden accounts, or be unloaded.
    const accountCount = new Set(sortedTxns.map((t) => t.accountLink?.id).filter(Boolean)).size;
    return { sortedTxns, balanceByPosition, currency, accountCount, firstRealActivityMs };
  }

  /**
   * Returns aligned + reval transactions for an intentionally unfiltered balance view.
   * The monthly summary always shows the full account picture regardless of any filters
   * the user may have active in the transactions view.
   * Note: revaluationTransactions.collection() reflects the most recently computed revals;
   * if CalculationPastStoreService skipped revals due to active filters, the collection may
   * be empty (first run with filters) or contain prior-run data (subsequent filtered runs).
   */
  private _getAllTransactionsForBalance(): TxnList {
    const aligned = this.transactionStore.alignedTransactionGeneratedCollection.collection() ?? [];
    const revals = this.calculationPastStore.revaluationTransactions.collection() ?? [];
    return [...aligned, ...revals];
  }

  private _buildCategoryMap(): Map<string, string> {
    const map = new Map<string, string>();
    (this.userStore.categories.categoryViews() ?? []).forEach((cat) => {
      map.set(cat.id, cat.name);
    });
    return map;
  }

  private _computeMonthStats(
    year: number,
    month: number,
    data: BalanceData,
    categoryMap: Map<string, string>,
  ): MonthlyStats {
    const { sortedTxns, balanceByPosition, currency, accountCount, firstRealActivityMs } = data;
    const monthStart = new Date(year, month, 1);
    const monthEnd = endOfMonth(monthStart);

    // For in-progress months, cap the stats window at today so that totalIn/totalOut
    // and closingBalance both cover the same [monthStart, effectiveEnd] window.
    const now = new Date();
    const effectiveEnd = monthEnd < now ? monthEnd : now;

    let totalIn = 0;
    let totalOut = 0;
    const categoryTotals = new Map<string, number>();

    const monthStartMs = monthStart.getTime();
    const effectiveEndMs = effectiveEnd.getTime();

    // Binary-search for the first transaction at or after monthStart.
    // Avoids an O(n) continue-pass over all historical entries (same comparator as
    // _lastBalance, so same-day ordering stays consistent with balanceByTxnId).
    let lo = 0;
    let hi = sortedTxns.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (toPostDateMs(sortedTxns[mid].postDate) < monthStartMs) {
        lo = mid + 1;
      } else {
        hi = mid;
      }
    }
    const windowStart = lo;

    // Read once: value can't change mid-loop, and reading it here keeps this method from
    // reactively subscribing to the signal per-transaction.
    const includeTransfers = this.includeTransfers();

    for (let i = windowStart; i < sortedTxns.length; i++) {
      const txn = sortedTxns[i];
      const txnMs = toPostDateMs(txn.postDate);

      // sortedTxns is ascending — once we pass effectiveEnd no later txn can be in range.
      if (txnMs > effectiveEndMs) {
        break;
      }

      // normalizedValue.amount is negative for Out transactions (sign baked in at storage time)
      const normalizedAmount = Number(txn.c_valuation?.normalizedValue?.amount ?? 0);
      const isIn = txn.direction === TransactionDirection.In;

      // Auto-detected transfers move money between the user's own accounts, so counting
      // them would double the IN/OUT totals and distort spending. Excluded from the
      // aggregation but not from isUserTransaction (a transfer is still real account
      // activity, so it must keep feeding firstRealActivityMs / hasAccountActivity).
      if (isUserTransaction(txn) && (includeTransfers || txn.c_linkType !== "transfer")) {
        if (isIn) {
          totalIn += Math.abs(normalizedAmount);
        } else {
          // normalizedAmount is negative for Out transactions (sign baked in at storage time).
          totalOut += Math.abs(normalizedAmount);
          const categoryId = txn.c_categories?.[0];
          const categoryName = categoryId
            ? (categoryMap.get(categoryId) ?? categoryId)
            : "Uncategorized";
          categoryTotals.set(
            categoryName,
            (categoryTotals.get(categoryName) ?? 0) + Math.abs(normalizedAmount),
          );
        }
      }
    }

    const openingBalance = this._lastBalance(sortedTxns, balanceByPosition, monthStart, false);
    const closingBalance = this._lastBalance(sortedTxns, balanceByPosition, effectiveEnd, true);

    const allCategories = Array.from(categoryTotals.entries())
      .sort(([, a], [, b]) => b - a)
      .map(([name, amount]) => ({ name, amount }));
    const topCategories = allCategories.slice(0, 5);

    const dailyBalances: number[] = [];
    let lowestBalance: MonthlyStats["lowestBalance"] = null;
    // A month that hasn't started yet (effectiveEnd = now, before monthStart) has no days.
    const lastDay = effectiveEnd < monthStart ? 0 : effectiveEnd.getDate();
    // Single forward pass: sortedTxns is ascending, so `pos` (index of the last txn on or before
    // the day's cutoff) only moves forward — same semantics as _lastBalance(inclusive).
    let pos = windowStart - 1;
    for (let day = 1; day <= lastDay; day++) {
      const date = new Date(year, month, day);
      const dayEnd = endOfDay(date);
      // Capped at effectiveEnd so the last day of an in-progress month matches closingBalance.
      const cutoffMs = Math.min(dayEnd.getTime(), effectiveEndMs);
      while (
        pos + 1 < sortedTxns.length &&
        toPostDateMs(sortedTxns[pos + 1].postDate) <= cutoffMs
      ) {
        pos++;
      }
      // pos < 0: nothing posted yet, so no account → NaN (not a balance, so it isn't plotted).
      // pos past the balances: incomplete worker result (already warned once via
      // closingBalance) → NaN.
      const balance = pos < 0 ? NaN : (balanceByPosition[pos] ?? NaN);
      dailyBalances.push(balance);
      // A day only counts toward "lowest" once any entry (an opening-balance entry included)
      // has posted; before that the day is NaN ("no account yet"), which this check skips.
      if (!Number.isNaN(balance) && (lowestBalance === null || balance < lowestBalance.amount)) {
        lowestBalance = { amount: balance, date };
      }
    }

    // firstRealActivityMs is account-level (computed once in _fetchBalanceData) — whether it
    // counts as activity "by this month" is the only part that varies per month.
    const hasAccountActivity =
      firstRealActivityMs !== null && firstRealActivityMs <= effectiveEndMs;

    return {
      month: monthStart,
      totalIn,
      totalOut,
      openingBalance,
      closingBalance,
      topCategories,
      allCategories,
      dailyBalances,
      lowestBalance,
      accountCount,
      currency,
      hasAccountActivity,
    };
  }

  /**
   * Returns the running balance of the last transaction relative to `cutoff`.
   * inclusive=false: strictly before cutoff (opening balance).
   * inclusive=true: on or before cutoff (closing balance).
   *
   * sortedTxns must be sorted with sortTransactionViews (same comparator used by
   * getBalancesByPosition) so that same-day ordering is consistent with balanceByPosition.
   * Binary search: O(log n) vs the naive reverse scan.
   */
  private _lastBalance(
    sortedTxns: TxnList,
    balanceByPosition: number[],
    cutoff: Date,
    inclusive: boolean,
  ): number {
    const cutoffMs = cutoff.getTime();
    let lo = 0;
    let hi = sortedTxns.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      const dMs = toPostDateMs(sortedTxns[mid].postDate);
      if (inclusive ? dMs <= cutoffMs : dMs < cutoffMs) {
        lo = mid + 1;
      } else {
        hi = mid;
      }
    }
    const idx = lo - 1;
    if (idx < 0) {
      return 0;
    }
    if (idx >= balanceByPosition.length) {
      // Worker returned incomplete results (e.g. empty balanceAmounts). Return NaN so the
      // caller can surface a "–" placeholder rather than displaying a misleading $0 balance.
      this.log.warning(
        `MonthlySummaryService._lastBalance: position ${idx} exceeds balance array length ${balanceByPosition.length} — worker returned incomplete results`,
      );
      return NaN;
    }
    return balanceByPosition[idx];
  }
}
